import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  XMarkIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import { MapPinIcon as MapPinSolidIcon } from '@heroicons/react/24/solid';

interface Memory {
  id: string;
  fact: string;
  category: string;
  importance: number;
  pinned: boolean;
  source: 'auto' | 'manual';
  createdAt: string;
}

interface MemoryPanelProps {
  isOpen: boolean;
  onClose: () => void;
  companionId?: string;
}

const CATEGORIES = [
  { value: 'personal', label: '👤', color: 'text-blue-400' },
  { value: 'preference', label: '⭐', color: 'text-yellow-400' },
  { value: 'interest', label: '💡', color: 'text-green-400' },
  { value: 'relationship', label: '💜', color: 'text-purple-400' },
];

const MemoryPanel: React.FC<MemoryPanelProps> = ({ isOpen, onClose, companionId }) => {
  const { t } = useTranslation();
  const [memories, setMemories] = useState<Memory[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newFact, setNewFact] = useState('');
  const [newCategory, setNewCategory] = useState('personal');
  const [filterCategory, setFilterCategory] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) fetchMemories();
  }, [isOpen, companionId, filterCategory]);

  const fetchMemories = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (companionId) params.companionId = companionId;
      if (filterCategory) params.category = filterCategory;
      const res = await axios.get('/memories', { params });
      setMemories(res.data);
    } catch (err) {
      console.error('Failed to fetch memories:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newFact.trim()) return;
    try {
      await axios.post('/memories', {
        fact: newFact.trim(),
        category: newCategory,
        companionId,
      });
      setNewFact('');
      setShowAddForm(false);
      toast.success(t('memory.added', 'Memory added'));
      fetchMemories();
    } catch (err) {
      toast.error(t('memory.addError', 'Failed to add memory'));
    }
  };

  const handleEdit = async (id: string) => {
    if (!editText.trim()) return;
    try {
      await axios.patch(`/memories/${id}`, { fact: editText.trim() });
      setEditingId(null);
      toast.success(t('memory.updated', 'Memory updated'));
      fetchMemories();
    } catch (err) {
      toast.error(t('memory.updateError', 'Failed to update'));
    }
  };

  const handleTogglePin = async (id: string) => {
    try {
      await axios.patch(`/memories/${id}/pin`);
      fetchMemories();
    } catch (err) {
      toast.error(t('memory.pinError', 'Failed to pin'));
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await axios.delete(`/memories/${id}`);
      toast.success(t('memory.deleted', 'Memory deleted'));
      fetchMemories();
    } catch (err) {
      toast.error(t('memory.deleteError', 'Failed to delete'));
    }
  };

  const getCategoryInfo = (cat: string) => CATEGORIES.find((c) => c.value === cat) || CATEGORIES[0];

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div className="fixed right-0 top-0 h-full w-96 max-w-[90vw] bg-dark-bg dark:bg-dark-bg bg-light-bg z-50 shadow-2xl flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-dark-border dark:border-dark-border border-light-border">
          <h2 className="text-lg font-semibold text-dark-text dark:text-dark-text text-light-text">
            🧠 {t('memory.title', 'Memories')}
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="p-1.5 rounded-full hover:bg-dark-card dark:hover:bg-dark-card hover:bg-light-surface transition-colors text-primary-400"
            >
              <PlusIcon className="w-5 h-5" />
            </button>
            <button
              onClick={onClose}
              className="p-1.5 rounded-full hover:bg-dark-card dark:hover:bg-dark-card hover:bg-light-surface transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Category filters */}
        <div className="flex gap-2 px-4 py-2 border-b border-dark-border/30">
          <button
            onClick={() => setFilterCategory(null)}
            className={`text-xs px-2 py-1 rounded-full transition-colors ${
              !filterCategory
                ? 'bg-primary-600 text-white'
                : 'bg-dark-card/50 text-dark-text-secondary hover:bg-dark-card'
            }`}
          >
            {t('memory.all', 'All')}
          </button>
          {CATEGORIES.map((cat) => (
            <button
              key={cat.value}
              onClick={() => setFilterCategory(filterCategory === cat.value ? null : cat.value)}
              className={`text-xs px-2 py-1 rounded-full transition-colors ${
                filterCategory === cat.value
                  ? 'bg-primary-600 text-white'
                  : 'bg-dark-card/50 text-dark-text-secondary hover:bg-dark-card'
              }`}
            >
              {cat.label} {t(`memory.category.${cat.value}`, cat.value)}
            </button>
          ))}
        </div>

        {/* Add form */}
        {showAddForm && (
          <div className="p-4 border-b border-dark-border/30 space-y-2">
            <textarea
              value={newFact}
              onChange={(e) => setNewFact(e.target.value)}
              placeholder={t(
                'memory.addPlaceholder',
                'Add a memory... (e.g., "My cat\'s name is Luna")',
              )}
              className="w-full px-3 py-2 rounded-lg bg-dark-card/50 dark:bg-dark-card/50 bg-light-surface/50 text-dark-text dark:text-dark-text text-light-text text-sm border border-dark-border/30 focus:border-primary-500 focus:outline-none resize-none"
              rows={2}
            />
            <div className="flex items-center justify-between">
              <select
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                className="text-xs px-2 py-1 rounded bg-dark-card/50 text-dark-text-secondary border border-dark-border/30 focus:outline-none"
              >
                {CATEGORIES.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label} {cat.value}
                  </option>
                ))}
              </select>
              <button
                onClick={handleAdd}
                disabled={!newFact.trim()}
                className="text-xs px-3 py-1.5 rounded-lg bg-primary-600 text-white hover:bg-primary-500 transition-colors disabled:opacity-50"
              >
                {t('memory.add', 'Add')}
              </button>
            </div>
          </div>
        )}

        {/* Memory list */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {loading ? (
            <div className="text-center text-dark-text-secondary py-8">
              {t('common.loading', 'Loading...')}
            </div>
          ) : memories.length === 0 ? (
            <div className="text-center text-dark-text-secondary py-8">
              <p className="text-2xl mb-2">🧠</p>
              <p className="text-sm">
                {t(
                  'memory.empty',
                  'No memories yet. Chat with your companion to build them, or add your own.',
                )}
              </p>
            </div>
          ) : (
            memories.map((memory) => {
              const catInfo = getCategoryInfo(memory.category);
              return (
                <div
                  key={memory.id}
                  className={`p-3 rounded-lg border transition-colors ${
                    memory.pinned
                      ? 'border-primary-500/40 bg-primary-900/10'
                      : 'border-dark-border/20 bg-dark-card/30'
                  }`}
                >
                  {editingId === memory.id ? (
                    <div className="space-y-2">
                      <textarea
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        className="w-full px-2 py-1.5 rounded bg-dark-card/50 text-dark-text text-sm border border-dark-border/30 focus:border-primary-500 focus:outline-none resize-none"
                        rows={2}
                        autoFocus
                      />
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => setEditingId(null)}
                          className="text-xs px-2 py-1 text-dark-text-secondary hover:text-dark-text"
                        >
                          {t('common.cancel', 'Cancel')}
                        </button>
                        <button
                          onClick={() => handleEdit(memory.id)}
                          className="text-xs px-2 py-1 bg-primary-600 text-white rounded hover:bg-primary-500"
                        >
                          {t('common.save', 'Save')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-start gap-2">
                        <span className={`text-sm ${catInfo.color}`}>{catInfo.label}</span>
                        <p className="flex-1 text-sm text-dark-text dark:text-dark-text text-light-text">
                          {memory.fact}
                        </p>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-dark-text-secondary">
                          {memory.source === 'manual' ? '✍️' : '🤖'}{' '}
                          {new Date(memory.createdAt).toLocaleDateString()}
                        </span>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleTogglePin(memory.id)}
                            className="p-1 rounded hover:bg-dark-card/50 transition-colors"
                            title={
                              memory.pinned
                                ? t('memory.unpin', 'Unpin')
                                : t('memory.pin', 'Pin (always active)')
                            }
                          >
                            {memory.pinned ? (
                              <MapPinSolidIcon className="w-4 h-4 text-primary-400" />
                            ) : (
                              <MapPinIcon className="w-4 h-4 text-dark-text-secondary" />
                            )}
                          </button>
                          <button
                            onClick={() => {
                              setEditingId(memory.id);
                              setEditText(memory.fact);
                            }}
                            className="p-1 rounded hover:bg-dark-card/50 transition-colors"
                          >
                            <PencilIcon className="w-4 h-4 text-dark-text-secondary" />
                          </button>
                          <button
                            onClick={() => handleDelete(memory.id)}
                            className="p-1 rounded hover:bg-red-900/30 transition-colors"
                          >
                            <TrashIcon className="w-4 h-4 text-dark-text-secondary hover:text-red-400" />
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Footer stats */}
        <div className="px-4 py-2 border-t border-dark-border/30 text-xs text-dark-text-secondary flex justify-between">
          <span>
            {memories.length} {t('memory.count', 'memories')}
          </span>
          <span>
            📌 {memories.filter((m) => m.pinned).length} {t('memory.pinned', 'pinned')}
          </span>
        </div>
      </div>
    </>
  );
};

export default MemoryPanel;
